import React, {
  BaseSyntheticEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { Box, Checkbox, Typography } from '@mui/material';
import { ReportsToolbox } from './reportsToolbox';
import { AppRow } from '../../layout/appRow';
import { ColorPalette, TypographyFamily } from '../../styles/constants';
import {
  useReportsFilters,
  useReportsFiltersDispatch,
  useReportsViewMode,
} from '../../context/reports/useReportsCtx';
import { ReportsEmployeeListMobile } from './reportsEmployeeListMobile';
import { ReportsProjectListMobile } from './reportsProjectListMobile';
import { FiltersFormData, ViewMode } from '../../models/reports';

const styles = {
  allocationsRow: {
    pr: 2,
    pl: 2,
    mb: 2,
    justifyContent: 'flex-end',
    alignItems: 'end',
    gap: 0.5,
  },
  allocationsBtn: {
    textDecorationColor: ColorPalette.GREEN,
    fontFamily: TypographyFamily.NEXA,
    marginLeft: '16px',
  },
  checkboxContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
  },
  listRow: {
    mb: 2,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    mr: 0,
    pr: 0,
  },
};

interface ReportsPageSmallLayoutProps {
  handleDisplay: (data: FiltersFormData) => void;
  handleUnloggedProjectsCheck?: (viewUnloggedProjects: boolean) => void;
}

export enum SmallLayoutRouter {
  FILTERS = 'filters',
  RESULTS = 'results',
}

export function ReportsPageSmallLayout({
  handleDisplay,
  handleUnloggedProjectsCheck,
}: ReportsPageSmallLayoutProps): JSX.Element {
  const [route, setRoute] = useState(SmallLayoutRouter.FILTERS);
  const [viewUnloggedEmployees, setViewUnloggedEmployees] = useState(false);
  const [viewUnloggedProjects, setViewUnloggedProjects] = useState(true);

  const filters = useReportsFilters();
  const dispatchFilters = useReportsFiltersDispatch();
  const [viewMode] = useReportsViewMode();

  const isResultsView = useMemo(
    () => route === SmallLayoutRouter.RESULTS,
    [route]
  );
  const isFiltersView = useMemo(
    () => route === SmallLayoutRouter.FILTERS,
    [route]
  );

  const handleDisplayAndNavigate = useCallback(
    (data: FiltersFormData) => {
      if (data?.project?.id) {
        setViewUnloggedEmployees(false);
      }

      handleDisplay(data);
      setRoute(SmallLayoutRouter.RESULTS);
    },
    [handleDisplay]
  );

  const handleViewUnloggedEmployees = useCallback((e: BaseSyntheticEvent) => {
    setViewUnloggedEmployees(e.target.checked);
  }, []);

  const handleViewShowInactiveEmployees = useCallback(
    (e: BaseSyntheticEvent) => {
      dispatchFilters({ ...filters, includeInactive: e.target.checked });
    },
    [dispatchFilters, filters]
  );

  const handleViewUnloggedProjects = useCallback(
    (e: BaseSyntheticEvent) => {
      if (handleUnloggedProjectsCheck) {
        handleUnloggedProjectsCheck(e.target.checked);
      }
      setViewUnloggedProjects(e.target.checked);
    },
    [handleUnloggedProjectsCheck]
  );

  const handleViewDirectSubordinates = useCallback(
    (e: BaseSyntheticEvent) => {
      dispatchFilters({ ...filters, showHierarchy: e.target.checked });
    },
    [dispatchFilters, filters]
  );

  const isUnloggedEmployeesCheckbox = useMemo(
    () => !filters?.projectId && viewMode === ViewMode.PEOPLE,
    [filters?.projectId, viewMode]
  );

  const isUnloggedProjects = useMemo(
    () => viewMode === ViewMode.PROJECT,
    [viewMode]
  );

  return (
    <>
      <Box display={isFiltersView ? 'initial' : 'none'}>
        <Box id="toolbox">
          <ReportsToolbox
            handleDisplay={handleDisplayAndNavigate}
            viewUnloggedProjects={viewUnloggedProjects}
          />
        </Box>

        <Box id="allocations">
          <AppRow sx={styles.allocationsRow}>
            {viewMode === ViewMode.PEOPLE && (
              <>
                <Box sx={styles.checkboxContainer}>
                  <Typography>Show indirect employees</Typography>
                  <Checkbox
                    onChange={handleViewDirectSubordinates}
                    checked={filters?.showHierarchy}
                  />
                </Box>
                <Box sx={styles.checkboxContainer}>
                  <Typography>Show also inactive</Typography>
                  <Checkbox
                    onChange={handleViewShowInactiveEmployees}
                    checked={filters?.includeInactive || false}
                    sx={{ mr: '-9px' }}
                  />
                </Box>
                {isUnloggedEmployeesCheckbox && (
                  <Box sx={styles.checkboxContainer}>
                    <Typography>Only discrepancies</Typography>
                    <Checkbox
                      onChange={handleViewUnloggedEmployees}
                      checked={viewUnloggedEmployees}
                      sx={{ mr: '-9px' }}
                    />
                  </Box>
                )}
              </>
            )}
            {isUnloggedProjects && (
              <Box sx={styles.checkboxContainer}>
                <Typography>Show only projects with logged time</Typography>
                <Checkbox
                  onChange={handleViewUnloggedProjects}
                  checked={viewUnloggedProjects}
                  sx={{ mr: '-9px' }}
                />
              </Box>
            )}
          </AppRow>
        </Box>
      </Box>

      {isResultsView && filters && (
        <AppRow sx={styles.listRow}>
          {viewMode === ViewMode.PEOPLE ? (
            <ReportsEmployeeListMobile
              filters={filters}
              viewMode={ViewMode.PEOPLE}
              viewUnloggedEmployees={viewUnloggedEmployees}
              viewShowInactiveEmployees={filters.includeInactive || false}
              setRoute={setRoute}
            />
          ) : (
            <ReportsProjectListMobile
              filters={filters}
              viewMode={viewMode}
              setRoute={setRoute}
              viewUnloggedProjects={viewUnloggedProjects}
            />
          )}
        </AppRow>
      )}
    </>
  );
}
